import { useId } from "react";
import { cn } from "@/lib/cn";

// An ON/OFF switch for a setting that takes effect on its own — not a checkbox
// in a form that is submitted later.
//
// WHY A COMPONENT. Two pages had grown their own: the platform report used a
// bare `<input type="checkbox">` sized down to 4×8 (which each browser draws
// differently, and which reads as "tick this box" rather than "this is on"),
// and the company report had a hand-styled track that was not exposed as a
// switch to assistive tech at all. One control, one behaviour, one appearance.
//
// WHY A NATIVE CHECKBOX UNDERNEATH. `role="switch"` on a real
// `<input type="checkbox">` is the switch pattern: the browser already gives it
// focus, Space activation, the checked state and form semantics, and the role
// changes only how it is ANNOUNCED ("on/off" instead of "checked"). Rebuilding
// that on a <div> means re-implementing keyboard handling by hand and getting
// it subtly wrong.
//
// THE ROW IS NOT A LABEL. The visible text is tied to the input with
// htmlFor/id, never by wrapping the row in a <label>. A wrapping label forwards
// every click in its box — including the padding and the empty space beside the
// caption — to the control, which is how this setting used to flip when nobody
// meant to touch it. Label text and the control itself activate it; blank row
// space does not.
export default function Switch({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  // Shows "ON"/"OFF" beside the track. The state of a switch should be readable
  // without knowing which side the thumb sits on.
  showState = true,
  id,
  className,
}) {
  const autoId = useId();
  const inputId = id || autoId;
  const descriptionId = description ? `${inputId}-description` : undefined;

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-3 dark:border-white/10",
        disabled && "opacity-60",
        className
      )}
    >
      <div className="min-w-0">
        <label
          htmlFor={inputId}
          className={cn(
            "block text-sm font-medium text-slate-700 dark:text-slate-200",
            disabled ? "cursor-not-allowed" : "cursor-pointer"
          )}
        >
          {label}
        </label>
        {description && (
          <p id={descriptionId} className="mt-0.5 text-xs text-slate-400">
            {description}
          </p>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {showState && (
          // aria-hidden: the switch itself already announces its state, so a
          // screen reader would otherwise hear it twice.
          <span
            aria-hidden="true"
            className={cn(
              "w-7 text-right text-[0.6875rem] font-semibold uppercase tracking-wider",
              checked ? "text-brand-600 dark:text-brand-300" : "text-slate-400"
            )}
          >
            {checked ? "On" : "Off"}
          </span>
        )}
        {/* The <label> wraps ONLY the track, so the whole control is clickable
            while the row around it stays inert. */}
        <label className={cn("relative block", disabled ? "cursor-not-allowed" : "cursor-pointer")}>
          <input
            id={inputId}
            type="checkbox"
            role="switch"
            checked={checked}
            disabled={disabled}
            aria-describedby={descriptionId}
            onChange={(e) => onChange(e.target.checked)}
            className="peer sr-only"
          />
          <span
            className={cn(
              "block h-5 w-9 rounded-full bg-slate-300 transition-colors",
              "after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:shadow after:transition-transform",
              "peer-checked:bg-brand-600 peer-checked:after:translate-x-4",
              "peer-focus-visible:ring-4 peer-focus-visible:ring-brand-500/25",
              "dark:bg-white/20"
            )}
          />
        </label>
      </div>
    </div>
  );
}
